import React, { useEffect, useState } from 'react';
import axios from 'axios';
import 'babel-polyfill';
import VirtualistFilter from './virtualistfilter';
import VirtualistResults from './virtualistresults';
import { IProvider } from './interfaces';
import { $setText, addQueryParam, formatPhone } from './util'; // Added formatPhone just in case needed later
import Footer from './footer';
import { labels } from './labels';
import FormProgress from './formprogress';
import FadAlert from './fadalert';

function getParam(s: string) {
    let params = new URLSearchParams(window.location.search);
    let p = params.get(s);
    if (p === 'undefined' || p === 'null') {
        return '';
    }
    return p;
}

function getAllParam(s: string) {
    let params = new URLSearchParams(window.location.search);
    let p = params.getAll(s);
    return p;
}

const Virtualist = () => {
    const [providers, setProviders] = useState<IProvider[]>([]);
    const [filterOptions, setFilterOptions] = useState({ language: [], insurance: [], gender: [], clinicalInterest: [], insuranceMapping: {} });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalProviders, setTotalProviders] = useState(0);
    // --- NEW STATE for mobile filter visibility ---
    const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);

    // Parameter fetching remains the same...
    // Use state for filter values that can change interactively    
    const [clinic, setClinic] = useState(getParam('clinic'));
    const [npi, setNpi] = useState(getParam('npi'));
    const [reason, setReason] = useState(getParam('reason'));
    const [insurance, setInsurance] = useState(getParam('insurance'));
    const [gender, setGender] = useState(getAllParam('gender'));
    const [language, setLanguage] = useState(getAllParam('language'));
    const [clinicalInterest, setClinicalInterest] = useState(getAllParam('ci'));
    const [apptType, setApptType] = useState("1");
    const [providerName, setProviderName] = useState(getParam('name'));
    const [fad, setFad] = useState(getParam('fad'));    
    const [age, setAge] = useState(getParam('age'));
    const [who, setWho] = useState(getParam('who'));


    function filterInsurance(providers: IProvider[], insuranceMapping) {
        for (let i = 0; i < providers.length; i++) {
            if (providers[i].insurance) {
                providers[i].insuranceList = providers[i].insurance.split("|");
            } else {
                providers[i].insuranceList = [];
            }
        }
        let selectedInsurance = getParam('insurance');
        if (selectedInsurance) {
            return providers.filter(p => p.insuranceList.map(i => insuranceMapping[i]).indexOf(selectedInsurance) >= 0);
        }
        return providers;
    }

    function filterGender(providers: IProvider[]) {
        let g = getAllParam('gender');
        if (g && g.length > 0) {
            let r = providers.filter(p => g.indexOf(p.gender) >= 0);
            return r;
        }
        return providers;
    }

    function filterLanguage(providers: IProvider[]) {
        let g = getAllParam('language');
        if (g && g.length > 0) {
            let r = [];
            for (let i = 0; i < providers.length; i++) {
                for (let j = 0; j < g.length; j++) {
                    if (providers[i].languageList.indexOf(g[j]) >= 0 && r.indexOf(providers[i]) < 0) {
                        r.push(providers[i]);
                    }
                }
            }
            return r;
        }
        return providers;
    }

    function filterClinicalInterest(providers: IProvider[]) {
        let g = getAllParam('ci');
        if (g && g.length > 0) {
            let r = [];
            for (let i = 0; i < providers.length; i++) {
                for (let j = 0; j < g.length; j++) {
                    if (providers[i].clinicalInterests.split("|").indexOf(g[j]) >= 0 && r.indexOf(providers[i]) < 0) {
                        r.push(providers[i]);
                    }
                }
            }
            return r;
        }
        return providers;
    }

    function filter(providers: IProvider[], insuranceMapping) {
        let p = filterInsurance(providers, insuranceMapping);
        p = filterGender(p);
        p = filterLanguage(p);
        p = filterClinicalInterest(p);
        return p;
    }

    // getProviders - fetch based on URL params (which are set by updateUrlAndFetch)
    function getProviders(insuranceMapping) {
        let currentUrlParams = new URLSearchParams(window.location.search);
        currentUrlParams.set("clinic", "Virtualists");
        let query = 'api/providers';
        let paramsString = currentUrlParams.toString();
        if (paramsString) {
            query += `?${paramsString}`;
        }

        setIsLoading(true);
        axios.get(query)
            .then((result) => {
                // Ensure data is an array
                const fetchedProviders = Array.isArray(result.data) ? result.data : [];
                setTotalProviders(fetchedProviders.length);
                preProcessProviders(fetchedProviders); // Pre-process before filtering
                // Filter based on the *current state* (insurance, gender, etc.)
                // not just URL params, because preProcessProviders might be needed first
                let f = filter(fetchedProviders, insuranceMapping);
                setProviders(f);
                setIsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching providers:", error);
                setProviders([]); // Clear providers on error
                setTotalProviders(0);
                setIsLoading(false);
            });
    }

    // useEffect to fetch initial options and providers based on initial URL
     useEffect(() => {
        let isMounted = true; // Prevent state update on unmounted component
         axios.get('api/filterOptions')
            .then((result) => {
                if (isMounted) {
                    setFilterOptions(result.data);
                    // Fetch providers based on the initial URL state
                    getProviders(result.data.insuranceMapping);
                }
             })
            .catch(error => {
                console.error("Error fetching filter options:", error);
                if (isMounted) setIsLoading(false);
            });
         return () => { isMounted = false; }; // Cleanup function
    }, []); // Run only on mount
   
    function preProcessProviders(providers: IProvider[]) {
        if (providers) {
            for (let i = 0; i < providers.length; i++) {
                if (providers[i].language) {
                    providers[i].languageList = providers[i].language.split("|");
                } else {
                    providers[i].languageList = [];
                }
                // Ensure English is added only if not present
                if (providers[i].languageList.indexOf("English") < 0) {
                    // Use unshift for better performance adding to the start
                    providers[i].languageList.unshift('English');
                }
            }
        }
    }

    function handleInsuranceChange(e: React.ChangeEvent<HTMLSelectElement>) {
        const newInsurance = e.currentTarget.value;
        setInsurance(newInsurance); // Update state
        updateUrlAndFetch({ insurance: newInsurance }); // Update URL and Fetch
    }

    function handleApptTypeChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newApptType = e.currentTarget.value;
        if (newApptType == "1") {
            window.location.href = "./";
            return;
        }
        setApptType(newApptType); // Update state
        updateUrlAndFetch({ apptType: newApptType }); // Update URL and Fetch
    }

    // applyFilters reads checkbox values, updates state, URL and fetches
    function applyFilters() {
        let newGender = [];
        let newLang = [];
        let newCI = [];

        const genderContainer = document.getElementById("filterGender");
        if (genderContainer) {
             const cbGender = Array.from(genderContainer.getElementsByTagName("input"));
             newGender = cbGender.filter(c => (c as HTMLInputElement).checked).map(c => c.value);
        }
        const langContainer = document.getElementById("filterLanguage");
        if (langContainer) {
            const cbLang = Array.from(langContainer.getElementsByTagName("input"));
            newLang = cbLang.filter(c => (c as HTMLInputElement).checked).map(c => c.value);
        }
        const ciContainer = document.getElementById("filterClinicalInterest");
        if(ciContainer) {
            const cbCI = Array.from(ciContainer.getElementsByTagName("input"));
            newCI = cbCI.filter(c => (c as HTMLInputElement).checked).map(c => c.value);
        }

        // Update state
        setGender(newGender);
        setLanguage(newLang);
        setClinicalInterest(newCI);

        // Update URL and fetch
        updateUrlAndFetch({
            gender: newGender,
            language: newLang,
            ci: newCI
        });

        setShowMobileFilters(false);
    }
     
    function clearDetailFilters() {
        // Clear the checkboxes visually
        const filterPane = document.getElementById('filter-pane');
        if (filterPane) {
            const checkboxes = Array.from(filterPane.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
            checkboxes.forEach(cb => cb.checked = false);
        }

        // Clear state variables
        setInsurance("");
        setGender([]);
        setLanguage([]);
        setClinicalInterest([]);

        // Update URL without these params and fetch
        updateUrlAndFetch({ gender: [], language: [], ci: [], insurance: "" });
        setShowMobileFilters(false); // Hide mobile filter pane
    }


    // Helper function to update URL and fetch providers
    // Takes an object of parameters to update/set
    function updateUrlAndFetch(paramsToUpdate: Record<string, any>) {
         const currentParams = new URLSearchParams(window.location.search);

         // Update parameters based on the input object
         Object.entries(paramsToUpdate).forEach(([key, value]) => {
            currentParams.delete(key); // Remove existing keys first for multi-value params
             if (Array.isArray(value)) {
                 value.forEach(val => currentParams.append(key, val));
            } else if (value !== null && value !== undefined && value !== '') {
                currentParams.set(key, value);
            }
         });

        const newQueryString = currentParams.toString();
        history.pushState(null, "", window.location.pathname + "?" + newQueryString);

        // Re-fetch data based on the new URL state
        getProviders(filterOptions.insuranceMapping);
    }

    return (<>
        {false && fad && age && parseInt(age) > 0 && <FadAlert age={age} />}
        <div className="container bg-white px-4 px-sm-5">
            {/* Header Section (Logo, Progress) */}
            <div className="pt-4 row">
                <div className="col-lg-4">
                    <a href="./"><img className="d-block" src="img/beth-israel-lahey-health-logo-web-crop2.png" alt="Beth Israel Lahey Health" style={{ maxWidth: "320px" }} /></a>
                </div>
                <div className="col-lg-8" style={{ marginTop: "18px" }}>
                    <FormProgress currentStep={1} />
                </div>
            </div>

            {/* Alerts */}
            {fad &&
                <div className="alert alert-info my-4 text-center" role="alert">
                    {providers.length > 0 && labels.search.fad(providers[0].ageCutoff)}
                </div>
            }
            {!fad && npi && providers.length > 0 && age && providers[0].ageCutoff && parseInt(age) < providers[0].ageCutoff &&
                <div className="alert alert-info my-4" role="alert">
                    {labels.search.ageCutoff(providers[0].ageCutoff)}
                </div>
            }        
            
             <div className="filter-row row pt-1 mb-2 mr-1 ml-1 bg-light p-3 rounded mb-4">
                {/* Appointment Type (Always Visible) */}
                <div className="form-inline mr-sm-4 col-sm-auto d-flex align-items-center"> {/* Use flex for alignment */}
                    <div className="btn-toolbar">
                        <div className="" style={{marginRight: "2rem"}}>
                        <span className="mr-2 mb-1 mb-sm-0" id="vistTypeGroupLabel">{labels.search.appointmentType}</span>
                        <div className="btn-group" role="radiogroup" aria-labelledby="vistTypeGroupLabel">
                                <input type="radio" className="btn-check" name="visitType" id="visitTypeInPerson" autoComplete="off" checked={false} onChange={handleApptTypeChange} value="1" />
                                <label className="btn btn-outline-primary btn-sm" htmlFor="visitTypeInPerson">{labels.search.inPerson}</label> {/* Smaller buttons */}
                            <input type="radio" className="btn-check" name="visitType" id="visitTypeVirtual" autoComplete="off" checked={true} onChange={handleApptTypeChange} value="2" />
                                <label className="btn btn-outline-primary btn-sm" htmlFor="visitTypeVirtual">{labels.search.virtualVisit}</label> {/* Smaller buttons */}
                                </div>
                        </div>
                        <div className="">
                        <span className="mr-2 mb-1 mb-sm-0">&nbsp;</span>
                        <div className="btn-group">
                            {/* --- Mobile Filter Toggle Button --- */}
                            {!npi && ( // Only show if not an NPI search
                                <button
                                    className="btn btn-primary d-md-none mb-2 order-2" // Visible only on mobile
                                    type="button"
                                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                                    aria-controls="filter-pane"
                                    aria-expanded={showMobileFilters}
                                >
                                    Filters {showMobileFilters ? <>&times;</> : <>&darr;</>}{/* Indicate state */}
                                </button>
                            )}
                            </div>
                        </div>
                    </div>
                </div>               
             </div>


            <hr className="mt-4 mb-1" />           
            <div className="row mt-4">
                <div className="col">
                    {providers && providers.length > 0 && !isLoading && // Show only when results are loaded
                        <div className="text-center w-100 mt-0 pt-0 mb-4"><b>{labels.search.instruction}</b><br /><span style={{ fontSize: "90%" }}>{labels.general.callcenter}</span></div>
                    }
                </div>
            </div>

            <div className="row mt-2 mt-md-4">
                                
                {!npi && (
                    // Adjusted classes: Hidden on mobile unless showMobileFilters=true, always block on md+
                    <div
                        id="filter-pane"
                        className={`col-12 col-md-4 col-lg-3 ${showMobileFilters ? 'd-block mb-4' : 'd-none'} d-md-block`}
                    >
                        {/* Sticky positioning only for desktop */}
                         <div className="d-md-sticky" style={{ top: '20px' }}>
                             <h4 className="d-none d-md-block">Filter By</h4>
                             <hr className="d-none d-md-block" />                             
                            <VirtualistFilter
                                options={filterOptions}
                                gender={gender}
                                language={language}
                                clinicalInterest={clinicalInterest}
                                // Props for mobile filters
                                insurance={insurance}
                                onInsuranceChange={handleInsuranceChange}
                                onCheckboxChange={applyFilters} // Pass applyFilters as the handler
                                npi={npi}
                            />
                             {/* Apply and Clear buttons */}
                            <div className="mt-3 d-flex justify-content-between">
                                {/* 
                                <button type="button" className="btn btn-primary" onClick={applyFilters}>
                                    Apply Filters
                                </button>*/}
                                <button type="button" className="btn btn-outline-secondary" onClick={clearDetailFilters}>
                                     Clear Filters
                                </button>
                             </div>
                             <hr className="d-md-none mt-4" /> {/* Separator on mobile below filters */}
                         </div>
                     </div>
                 )}                
                <div className={npi ? "col-12" : "col-12 col-md-8 col-lg-9"}>
                    {isLoading && <div className="text-center my-5">Loading...</div>}
                    {!isLoading && providers.length === 1 && providers[0]?.npi === "__PRACTICE__"
                        ? <div>Please call clinic office at <a href={"tel:" + providers[0].phone}>{providers[0].phone}</a> to speak with the clinic staff to schedule an appointment.</div>
                        : <VirtualistResults
                            totalProviders={totalProviders}
                            providers={providers}                            
                            isLoading={isLoading}
                            reason={reason}
                            apptType={apptType}
                            who={who}
                            backto={encodeURIComponent(window.location.search)} // Keep backto
                          />
                     }
                </div>
            </div>

        </div>
        <Footer />
    </>);
}

export default Virtualist;